import type { ChartArea, Plugin, Scale } from "chart.js";

import type { ChartMarkerLine } from "../TargetChart.types";

/**
 * Line color per marker variant. A full `Record` so adding a variant without
 * its color is a compile error.
 */
export type MarkerColors = Record<ChartMarkerLine["variant"], string>;

/** The bit of Chart.js's x scale the marker positioning needs. */
type MarkerXScale = Pick<Scale, "getPixelForValue">;

/**
 * Horizontal position of a marker line, in pixels.
 *
 * External events sit *on* the session they happened in. Phase lines sit
 * *between* sessions: the new conditions apply from the next session onwards,
 * so drawing the line on the point would suggest the session itself is already
 * part of the new phase. When the anchor is the last session there is no next
 * point, so the line is placed half a step past it, reusing the previous gap —
 * unless it's also the only session, where there is no gap to reuse.
 *
 * A fractional index already sits between sessions, where the marker's date
 * falls, so it's drawn as is: offsetting it would move it off its own date.
 *
 * @param marker    - The marker being drawn
 * @param xScale    - The chart's x scale
 * @param lastIndex - Index of the last plotted point, or -1 when unknown
 */
export function resolveMarkerX(
  marker: ChartMarkerLine,
  xScale: MarkerXScale,
  lastIndex: number
): number {
  const anchorX = xScale.getPixelForValue(marker.index);
  if (marker.variant !== "phase-change" || !Number.isInteger(marker.index)) return anchorX;

  const nextX =
    lastIndex < 0 || marker.index < lastIndex
      ? xScale.getPixelForValue(marker.index + 1)
      : Number.NaN;
  if (Number.isFinite(nextX)) return (anchorX + nextX) / 2;

  // Single-session chart: no gap to mirror. Category scales extrapolate a
  // finite pixel for index -1, which would push the line half an empty chart to
  // the right, so sit on the session instead — the next session settles it.
  if (marker.index <= 0) return anchorX;

  // Last session: mirror the gap to its left so the offset matches the rest.
  const previousX = xScale.getPixelForValue(marker.index - 1);
  return Number.isFinite(previousX) ? anchorX + (anchorX - previousX) / 2 : anchorX;
}

/** The bit of Chart.js's chart area the marker placement needs. */
type MarkerChartArea = Pick<ChartArea, "left" | "right">;

/**
 * Horizontal distance, in pixels, within which the pointer counts as being on a
 * marker line. Lines are 1.5px wide, so a few pixels either side keeps them
 * comfortably hoverable and clickable.
 */
export const MARKER_HIT_TOLERANCE = 8;

/**
 * Where a marker line is actually drawn, or `null` when it isn't drawn at all.
 *
 * Shared by drawing and hit-testing so a click always lands on the line the
 * clinician sees — including phase lines offset between sessions and lines
 * clamped to the plot area.
 *
 * @param marker    - The marker to place
 * @param xScale    - The chart's x scale
 * @param chartArea - The plot area bounds
 * @param lastIndex - Index of the last plotted point, or -1 when unknown
 */
export function markerDrawX(
  marker: ChartMarkerLine,
  xScale: MarkerXScale,
  chartArea: MarkerChartArea,
  lastIndex: number
): number | null {
  const anchorX = xScale.getPixelForValue(marker.index);
  // Skip markers whose session falls outside the plot area so lines never
  // draw over the axes or margins. Checked on the session's own position, not
  // the drawn one, so a phase line on the last session isn't dropped for
  // sitting half a step past it.
  if (!Number.isFinite(anchorX) || anchorX < chartArea.left || anchorX > chartArea.right) {
    return null;
  }

  // Phase lines are drawn between sessions; the offset can land just outside
  // the plot area, so keep it within bounds.
  return Math.min(
    Math.max(resolveMarkerX(marker, xScale, lastIndex), chartArea.left),
    chartArea.right
  );
}

/**
 * The marker line under the pointer, if any.
 *
 * Picks the closest line within `tolerance`. On a tie the marker later in the
 * list wins, since it's drawn last and so sits on top.
 *
 * @param markers     - Markers currently on the chart
 * @param xScale      - The chart's x scale
 * @param chartArea   - The plot area bounds
 * @param lastIndex   - Index of the last plotted point, or -1 when unknown
 * @param pointerX    - Pointer x position, in canvas pixels
 * @param [tolerance] - Max horizontal distance to count as a hit
 */
export function findMarkerAt(
  markers: ChartMarkerLine[],
  xScale: MarkerXScale,
  chartArea: MarkerChartArea,
  lastIndex: number,
  pointerX: number,
  tolerance: number = MARKER_HIT_TOLERANCE
): ChartMarkerLine | null {
  let hit: ChartMarkerLine | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const marker of markers) {
    const x = markerDrawX(marker, xScale, chartArea, lastIndex);
    if (x == null) continue;

    const distance = Math.abs(pointerX - x);
    // `<=` so a later (topmost) marker wins a tie.
    if (distance <= tolerance && distance <= closestDistance) {
      hit = marker;
      closestDistance = distance;
    }
  }

  return hit;
}

/** Index of the last plotted point, or -1 when the chart has no labels yet. */
const lastLabelIndex = (data?: { labels?: unknown[] }) => (data?.labels?.length ?? 0) - 1;

/** The bits of a Chart.js instance pointer hit-testing needs. */
export interface MarkerHitChart {
  canvas: Pick<HTMLCanvasElement, "getBoundingClientRect"> | null;
  chartArea?: Pick<ChartArea, "left" | "right" | "top" | "bottom">;
  scales?: { x?: MarkerXScale };
  data?: { labels?: unknown[] };
}

/**
 * Where a marker's line is drawn on a laid-out chart, in canvas pixels, or null
 * when it isn't drawn (or the chart hasn't laid out yet). Same geometry the
 * plugin draws with, so callers can anchor UI — a tooltip, say — to the line.
 *
 * @param chart  - The rendered Chart.js instance, or null before mount
 * @param marker - The marker to place
 */
export function markerDrawXOnChart(
  chart: MarkerHitChart | null,
  marker: ChartMarkerLine
): number | null {
  const xScale = chart?.scales?.x;
  const chartArea = chart?.chartArea;
  if (!xScale || !chartArea) return null;
  return markerDrawX(marker, xScale, chartArea, lastLabelIndex(chart.data));
}

/**
 * The marker line under a pointer event, if any. Converts the viewport position
 * into canvas pixels and resolves the series length the same way drawing does.
 *
 * Lines only span the plot area vertically, so a pointer above or below it —
 * over the x-axis labels, say — never hits one, even when it's lined up with it.
 *
 * Returns null until the chart has laid out (no scale or plot area yet).
 *
 * @param chart   - The rendered Chart.js instance, or null before mount
 * @param markers - Markers currently on the chart
 * @param clientX - Pointer x position in viewport coordinates
 * @param clientY - Pointer y position in viewport coordinates
 */
export function findMarkerAtPointer(
  chart: MarkerHitChart | null,
  markers: ChartMarkerLine[],
  clientX: number,
  clientY: number
): ChartMarkerLine | null {
  const xScale = chart?.scales?.x;
  const chartArea = chart?.chartArea;
  if (!chart?.canvas || !chartArea || !xScale || markers.length === 0) return null;

  // Chart.js scales and `chartArea` are in CSS pixels — the device pixel ratio
  // is applied as a canvas transform — so the offset from the canvas's edges
  // is already in the right space, high-DPI screens included.
  const canvasRect = chart.canvas.getBoundingClientRect();
  const pointerY = clientY - canvasRect.top;
  if (pointerY < chartArea.top || pointerY > chartArea.bottom) return null;

  const pointerX = clientX - canvasRect.left;
  return findMarkerAt(markers, xScale, chartArea, lastLabelIndex(chart.data), pointerX);
}

/** Line width of a marker at rest, and while hovered. */
const MARKER_LINE_WIDTH = 1.5;
const MARKER_HOVER_LINE_WIDTH = 2.5;

/**
 * Draws the clinician-placed markers as vertical annotation lines: solid for
 * phase changes, dashed for external events. The line alone carries no text —
 * a marker's label, date and notes are read from the tooltip that opens when
 * the line is hovered, which keeps the chart legible however many markers a
 * target collects (pending design's take on showing them on the chart itself).
 *
 * Markers are read lazily, at draw time, so the plugin instance created on
 * mount keeps reflecting later marker changes without remounting the chart.
 *
 * @param getMarkers - Reads the current markers at draw time
 * @param colors     - Line color per marker variant
 * @param [getHoveredMarkerId] - Reads the hovered marker's id at draw time;
 *                               that line is drawn thicker
 */
export function buildMarkerPlugin(
  getMarkers: () => ChartMarkerLine[],
  colors: MarkerColors,
  getHoveredMarkerId: () => number | null = () => null
): Plugin<"line"> {
  return {
    id: "targetChartMarkers",
    afterDatasetsDraw(chart) {
      // Read markers lazily at draw time
      const markers = getMarkers();
      if (!markers.length) return;
      const { ctx, scales, chartArea } = chart;
      const xScale = scales["x"];
      if (!xScale || !chartArea) return;

      const lastIndex = lastLabelIndex(chart.data);
      const hoveredMarkerId = getHoveredMarkerId();

      ctx.save();
      markers.forEach((marker) => {
        const x = markerDrawX(marker, xScale, chartArea, lastIndex);
        if (x == null) return;

        const color = colors[marker.variant];

        // Vertical line — solid for phase changes, dashed for external events.
        ctx.strokeStyle = color;
        ctx.lineWidth = marker.id === hoveredMarkerId ? MARKER_HOVER_LINE_WIDTH : MARKER_LINE_WIDTH;
        ctx.setLineDash(marker.variant === "external-event" ? [5, 4] : []);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
      });
      ctx.restore();
    },
  };
}
